import { PayloadAction } from '@reduxjs/toolkit';
import { delay, put, select } from 'redux-saga/effects';

import * as API from 'services';

import { MyPageSelectors } from '../my-page';
import {
	formatApplicantList,
	formatProviderInfoList,
	formatUploadExcelLodgingFailedList,
	formatWorkerLodgingApplicantList,
	formatWorkerLodgingDetail,
	formatWorkerLodgingList,
} from './worker-lodging.helper';
import { WorkerLodgingActions } from './worker-lodging.redux';
import {
	IPayloadCreateLodgingApplicantRequest,
	IPayloadCreateLodgingRequest,
	IPayloadDeleteApplicantListByLodgingIdRequest,
	IPayloadDeleteWorkerLodgingByIdRequest,
	IPayloadDeleteWorkerLodgingsRequest,
	IPayloadGetApplicantListByLodgingIdRequest,
	IPayloadGetProviderInfoListRequest,
	IPayloadGetWorkerLodgingApplicantListRequest,
	IPayloadGetWorkerLodgingDetailRequest,
	IPayloadGetWorkerLodgingExcelListRequest,
	IPayloadGetWorkerLodgingListRequest,
	IPayloadUpdateLodgingProviderRequest,
	TPayloadUploadExcelLodgingRequest,
} from './worker-lodging.type';

export function* getWorkerLodgingListSaga(
	action: PayloadAction<IPayloadGetWorkerLodgingListRequest>
) {
	const { callback, searchTypes, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerLodgingListResponse> =
			yield API.getWorkerLodgingList({ searchTypes, ...rest });
		const workerLodgingList = formatWorkerLodgingList(res?.data?.content);

		yield put(
			WorkerLodgingActions.getWorkerLodgingListSuccess({
				...rest,
				workerLodgingList,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.getWorkerLodgingListFailure());
	}
}

export function* getWorkerLodgingExcelListSaga(
	action: PayloadAction<IPayloadGetWorkerLodgingExcelListRequest>
) {
	const { callback, searchTypes, ...rest } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerLodgingListResponse> =
			yield API.getWorkerLodgingList({ searchTypes, ...rest });
		const workerLodgingList = formatWorkerLodgingList(res?.data?.content);

		yield put(WorkerLodgingActions.getWorkerLodgingExcelListSuccess());
		callback?.({ data: workerLodgingList });
	} catch (error) {
		callback?.({ error });
		yield put(WorkerLodgingActions.getWorkerLodgingExcelListFailure());
	}
}

export function* getWorkerLodgingDetail(
	action: PayloadAction<IPayloadGetWorkerLodgingDetailRequest>
) {
	const { id, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetWorkerLodgingDetailResponse> =
			yield API.getWorkerLodgingDetail(id);

		const workerLodgingDetail = formatWorkerLodgingDetail(res?.data);

		yield put(
			WorkerLodgingActions.getWorkerLodgingDetailSuccess({
				workerLodgingDetail,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.getWorkerLodgingDetailFailure());
	}
}

export function* deleteWorkerLodgings(
	action: PayloadAction<IPayloadDeleteWorkerLodgingsRequest>
) {
	const { ids, callback } = action.payload;

	try {
		yield API.deleteWorkerLodgings(ids);

		yield put(WorkerLodgingActions.deleteWorkerLodgingsSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.deleteWorkerLodgingsFailure());
	}
}

export function* getProviderInfoList(
	action: PayloadAction<IPayloadGetProviderInfoListRequest>
) {
	const { callback, params } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.GetProviderInfoListResponse> =
			yield API.getProviderInfoList(params);

		const providerInfoList = formatProviderInfoList(res?.data);

		yield put(
			WorkerLodgingActions.getProviderInfoListSuccess({
				providerInfoList,
				total: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.getProviderInfoListFailure());
	}
}

export function* createLodging(
	action: PayloadAction<IPayloadCreateLodgingRequest>
) {
	const { callback, ...props } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.IWorkerRecommenderWhenRequestCreateSuccess> =
			yield API.createLodging({
				...props.data,
			});

		yield put(WorkerLodgingActions.createLodgingSuccess());
		callback?.(res?.data as number);
	} catch (error) {
		callback?.(undefined, error as API.ApiResponseError);
		yield put(WorkerLodgingActions.createLodgingFailure());
	}
}

export function* deleteWorkerLodgingById(
	action: PayloadAction<IPayloadDeleteWorkerLodgingByIdRequest>
) {
	const { id, callback } = action.payload;

	try {
		yield API.deleteWorkerLodgingById(id);

		yield put(WorkerLodgingActions.deleteWorkerLodgingByIdSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.deleteWorkerLodgingByIdFailure());
	}
}

export function* getApplicantListByLodgingId(
	action: PayloadAction<IPayloadGetApplicantListByLodgingIdRequest>
) {
	const { callback } = action.payload;

	try {
		// const res: API.ApiResponseSuccess<API.GetApplicantListByLodgingIdResponse> =
		// 	yield API.getApplicantListByLodgingId(id, params);
		delay(1000);
		yield put(
			WorkerLodgingActions.getApplicantListByLodgingIdSuccess({
				data: formatApplicantList({} as any),
				total: 0,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.getApplicantListByLodgingIdFailure());
	}
}

export function* createLodgingApplicant(
	action: PayloadAction<IPayloadCreateLodgingApplicantRequest>
) {
	const { lodgingId, data, callback } = action.payload;

	try {
		yield API.createLodgingApplicant(lodgingId, data);

		yield put(WorkerLodgingActions.createLodgingApplicantSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.createLodgingApplicantFailure());
	}
}

export function* deleteApplicantListByLodgingId(
	action: PayloadAction<IPayloadDeleteApplicantListByLodgingIdRequest>
) {
	const { ids, lodgingId, callback } = action.payload;

	try {
		yield API.deleteApplicantListByLodgingId(lodgingId, ids);

		yield put(WorkerLodgingActions.deleteApplicantListByLodgingIdSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.deleteApplicantListByLodgingIdFailure());
	}
}

export function* uploadExcelLodgingSaga(
	action: PayloadAction<TPayloadUploadExcelLodgingRequest>
) {
	const { data, callback } = action.payload;

	try {
		const res: API.ApiResponseSuccess<API.TUploadExcelLodgingResponse> =
			yield API.uploadExcelLodging(data);

		const dataMyPage: ReturnType<typeof MyPageSelectors.selectDataMyPage> =
			yield select(MyPageSelectors.selectDataMyPage);
		const userInfo: API.UploadExcelUserInfo = {
			districtId: dataMyPage?.districtId,
			districtName: dataMyPage?.districtName,
			role: dataMyPage?.role,
		};

		const failedList = formatUploadExcelLodgingFailedList({
			input: res?.data,
			userInfo,
		});
		const successCount = data?.length - (failedList?.length || 0);
		const failCount = failedList?.length || 0;

		yield put(
			WorkerLodgingActions.uploadExcelLodgingSuccess({
				failedList,
				successCount,
				failCount,
			})
		);
		callback?.({
			failCount,
			successCount,
		});
	} catch (error: any) {
		callback?.({
			error,
			failCount: 0,
			successCount: 0,
		});
		yield put(WorkerLodgingActions.uploadExcelLodgingFailure());
	}
}

export function* getWorkerLodgingApplicantListSaga(
	action: PayloadAction<IPayloadGetWorkerLodgingApplicantListRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		// call api
		const res: API.ApiResponseSuccess<API.GetWorkerLodgingApplicantListResponse> =
			yield API.getWorkerLodgingApplicantList({
				...rest,
				programId: rest?.programId || '',
			});
		const applicantList = formatWorkerLodgingApplicantList(res?.data?.content);
		yield put(
			WorkerLodgingActions.getWorkerLodgingApplicantListSuccess({
				...rest,
				applicantList,
				applicantTotal: res?.data?.total,
			})
		);
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.getWorkerLodgingApplicantListFailure());
	}
}

export function* updateLodgingProviderSaga(
	action: PayloadAction<IPayloadUpdateLodgingProviderRequest>
) {
	const { callback, ...rest } = action.payload;

	try {
		yield API.updateLodgingProvider(rest);

		yield put(WorkerLodgingActions.updateLodgingProviderSuccess());
		callback?.();
	} catch (error) {
		callback?.(error);
		yield put(WorkerLodgingActions.updateLodgingProviderFailure());
	}
}
